import { FaEnvelope } from 'react-icons/fa';
import { Seo, buildArticleSchema } from '../../core/seo';

const termsArticleSchema = buildArticleSchema({
  headline: 'Terms of Service',
  description:
    'HirePanther Terms of Service. Read the binding agreement between you and HirePanther when using our platform.',
  url: 'https://uat.hirepanther.ca/terms-services',
  datePublished: '2025-12-01',
  dateModified: '2025-12-01',
  publisher: {
    '@type': 'Organization',
    name: 'HirePanther',
    url: 'https://uat.hirepanther.ca',
  },
});

const TermsOfService = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Terms of Service"
        description="HirePanther Terms of Service. Read the binding agreement between you and HirePanther when using our platform."
        canonicalPath="/terms-services"
        structuredData={termsArticleSchema}
      />
      <div className="max-w-5xl mx-auto p-6 text-[#284F60] bg-white">
        {/* ===== Page Title ===== */}
        <h1 className="text-3xl font-bold mb-6 text-[#284F60] flex items-center gap-3">
          Terms of Service
        </h1>

        {/* ===== Effective Dates ===== */}
        <p className="text-sm text-gray-600 mb-6">
          <strong>Effective Date:</strong> December 01, 2025 |{' '}
          <strong>Last Updated:</strong> December 01, 2025
        </p>

        {/* ===== Introduction ===== */}
        <p className="mb-4">
          Welcome to HirePanther. These Terms of Service (“Terms”) form a
          binding agreement between you (“you,” “user,” “customer,” or
          “provider”) and HirePanther Inc. (“HirePanther,” “we,” “our,” or
          “us”).
        </p>
        <p className="mb-6">
          By accessing or using the HirePanther platform (including our
          websites, mobile applications, and provider portal, collectively the
          “Platform”), you agree to these Terms. If you do not agree, you must
          not use the Platform.
        </p>

        {/* ===== Sections ===== */}
        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              1. Definitions
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                <strong>Customer:</strong> A user who books or purchases
                services through HirePanther.
              </li>
              <li>
                <strong>Provider:</strong> An independent business, contractor,
                or professional offering services through HirePanther.
              </li>
              <li>
                <strong>Services:</strong> Any service listed and booked on the
                Platform.
              </li>
              <li>
                <strong>Booking:</strong> A confirmed order for Services made
                through the Platform.
              </li>
              <li>
                <strong>Content:</strong> Text, images, reviews, and other
                material submitted or displayed on the Platform.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              2. Eligibility & Accounts
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>You must be at least 18 years old to use HirePanther.</li>
              <li>
                You are responsible for maintaining the accuracy of your account
                information.
              </li>
              <li>
                You are responsible for safeguarding your credentials. If your
                account is misused, you must notify us immediately.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              3. Role of HirePanther
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                HirePanther is a marketplace platform, not a service provider.
              </li>
              <li>
                Providers are independent businesses. They are not employees,
                agents, or partners of HirePanther.
              </li>
              <li>
                HirePanther does not control the quality, timing, legality, or
                safety of the Services.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              4. Bookings & Payments
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                Customers can request and confirm Bookings via the Platform.
              </li>
              <li>
                Payments are processed securely through our third-party partner
                (e.g., Stripe, Interac, or other).
              </li>
              <li>
                HirePanther holds customer funds until Services are completed,
                then releases payouts to Providers per our payout schedule.
              </li>
              <li>All off-platform payments are prohibited.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              5. Cancellations & Refunds
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                Cancellation policies may vary by Provider category. Customers
                will see the applicable policy at checkout.
              </li>
              <li>
                If a Provider cancels, customers may be entitled to a full
                refund.
              </li>
              <li>
                HirePanther may issue refunds at our discretion in cases of
                fraud, unsafe conduct, or policy violations.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              6. Provider Obligations
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                Comply with all applicable laws, licenses, and regulations.
              </li>
              <li>Maintain accurate listings with transparent pricing.</li>
              <li>
                Provide Services safely, professionally, and as described.
              </li>
              <li>Maintain appropriate insurance coverage where applicable.</li>
              <li>
                Not solicit or accept off-platform payments for Services listed
                on HirePanther.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              7. Customer Obligations
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Provide accurate booking details (address, time, scope).</li>
              <li>Ensure safe premises and reasonable access for Providers.</li>
              <li>Treat Providers respectfully.</li>
              <li>
                Not misuse the Platform or attempt to circumvent payments.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              8. Reviews & Ratings
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Customers may leave reviews after a Booking.</li>
              <li>Reviews must be honest, accurate, and non-defamatory.</li>
              <li>HirePanther may remove reviews that violate our policies.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              9. Prohibited Uses
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Use the Platform for illegal purposes.</li>
              <li>Post false, harmful, or infringing Content.</li>
              <li>
                Circumvent the Platform by arranging off-platform transactions.
              </li>
              <li>Harass, abuse, or endanger other users.</li>
              <li>Interfere with Platform security or operations.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              10. Insurance & Liability
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                HirePanther does not provide insurance coverage for Bookings.
              </li>
              <li>
                Providers are strongly encouraged (and in some cases required)
                to carry Commercial General Liability (CGL) or other appropriate
                coverage.
              </li>
              <li>
                Customers should verify insurance with Providers where relevant.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              11. Limitation of Liability
            </h2>
            <p>To the fullest extent permitted by law:</p>
            <ul className="list-disc list-inside space-y-1 mt-2 ml-4">
              <li>
                HirePanther is not liable for the acts or omissions of Providers
                or Customers.
              </li>
              <li>We make no warranties about the Services.</li>
              <li>
                Our total liability to you for any claim will not exceed the
                greater of:
                <br /> (a) CAD $100, or <br /> (b) the total fees you paid to
                HirePanther in the 6 months before the claim.
              </li>
            </ul>
            <p className="mt-2 text-sm text-[#284F60]">
              Some jurisdictions may not allow certain limitations, so parts of
              this section may not apply to you.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              12. Intellectual Property
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                All Platform content, branding, and software belong to
                HirePanther or its licensors.
              </li>
              <li>
                Users retain rights in their own uploaded Content but grant
                HirePanther a non-exclusive, worldwide, royalty-free license to
                use it for operating and promoting the Platform.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              13. Privacy
            </h2>
            <p>
              Your use of HirePanther is also governed by our Privacy Policy.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              14. Termination
            </h2>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>
                We may suspend or terminate accounts for violations of these
                Terms, fraud, or misconduct.
              </li>
              <li>
                Users may close their accounts at any time by contacting
                support.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              15. Disputes & Governing Law
            </h2>
            <p>
              These Terms are governed by the laws of Ontario, Canada, without
              regard to conflict of laws. Any disputes will be resolved in the
              courts of Toronto, Ontario, unless arbitration is agreed
              separately.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              16. Changes to Terms
            </h2>
            <p>
              We may update these Terms from time to time. Updates will be
              posted at /legal/terms with a new effective date. Continued use of
              the Platform means you accept the revised Terms.
            </p>
          </div>

          {/* ===== Contact Section ===== */}
          <div>
            <h2 className="text-xl font-semibold text-[#2D617A] mb-2">
              17. Contact Us
            </h2>
            <p>Questions about these Terms can be sent to:</p>
            <div className="mt-4 space-y-2">
              <p className="flex items-center gap-2">
                <FaEnvelope className="text-indigo-600" />
                <a
                  href="mailto:legal@hirepanther.ca"
                  className="text-blue-500 hover:underline"
                >
                  legal@hirepanther.ca
                </a>
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TermsOfService;
